// src/components/LeaveManagement.js
import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Form,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import {
  getAllLeaves,
  approveLeave,
  rejectLeave,
  LEAVE_STATUS,
  LEAVE_TYPES,
  calculateLeaveDays,
} from "../services/leaveService";
import { logActivity } from "../services/activityService";
import { useAuth } from "../context/AuthProvider";
import Loader from "./Loader";
import { CheckIcon, X } from "lucide-react";

export default function LeaveManagement() {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [actionType, setActionType] = useState(""); // "approve" or "reject"
  const [comments, setComments] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    leaveType: "",
    dateRange: "all",
  });

  useEffect(() => {
    fetchLeaves();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [leaves, filters]);

  const fetchLeaves = async () => {
    setLoading(true);
    try {
      const data = await getAllLeaves();
      setLeaves(data);
    } catch (error) {
      console.error("Error fetching leaves:", error);
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...leaves];

    if (filters.status) {
      filtered = filtered.filter((leave) => leave.status === filters.status);
    }

    if (filters.leaveType) {
      filtered = filtered.filter(
        (leave) => leave.leaveType === filters.leaveType
      );
    }

    if (filters.dateRange !== "all") {
      const now = new Date();
      const daysAgo = parseInt(filters.dateRange);
      const cutoffDate = new Date(
        now.getTime() - daysAgo * 24 * 60 * 60 * 1000
      );

      filtered = filtered.filter(
        (leave) => new Date(leave.appliedAt) >= cutoffDate
      );
    }

    setFilteredLeaves(filtered);
  };

  const handleAction = (leave, action) => {
    setSelectedLeave(leave);
    setActionType(action);
    setComments("");
    setShowModal(true);
  };

  const confirmAction = async () => {
    if (!selectedLeave) return;

    setActionLoading(true);
    try {
      let actionText = "";
      if (actionType === "approve") {
        await approveLeave(
          selectedLeave.id,
          user.id,
          user.name || user.email,
          comments
        );
        actionText = `Your leave (${selectedLeave.leaveType}) from ${new Date(selectedLeave.startDate).toLocaleDateString()} to ${new Date(selectedLeave.endDate).toLocaleDateString()} has been approved.`;
      } else if (actionType === "reject") {
        await rejectLeave(
          selectedLeave.id,
          user.id,
          user.name || user.email,
          comments
        );
        actionText = `Your leave (${selectedLeave.leaveType}) from ${new Date(selectedLeave.startDate).toLocaleDateString()} to ${new Date(selectedLeave.endDate).toLocaleDateString()} has been rejected.`;
      }

      // Log activity for the applicant
      await logActivity({
        userId: user.id,
        userRole: user.role,
        targetUserId: selectedLeave.userId,
        text: actionText,
        type: "leave",
        actionType: `${user.role}_action`,
        metadata: {
          userName: user.name || user.email,
          action: `${actionType}_leave`,
          leaveId: selectedLeave.id,
          leaveType: selectedLeave.leaveType,
          startDate: selectedLeave.startDate,
          endDate: selectedLeave.endDate,
          adminId: user.id,
          adminName: user.name || user.email,
          comments: comments || ""
        }
      });

      await fetchLeaves(); // Refresh the list
      setShowModal(false);
      setSelectedLeave(null);
      setComments("");
    } catch (error) {
      console.error("Error processing leave action:", error);
    }
    setActionLoading(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      [LEAVE_STATUS.PENDING]: { bg: "warning", text: "Pending" },
      [LEAVE_STATUS.APPROVED]: { bg: "success", text: "Approved" },
      [LEAVE_STATUS.REJECTED]: { bg: "danger", text: "Rejected" },
      [LEAVE_STATUS.CANCELLED]: { bg: "secondary", text: "Cancelled" },
    };

    const config = statusConfig[status] || { bg: "secondary", text: status };
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const getLeaveTypeBadge = (type) => {
    const typeConfig = {
      [LEAVE_TYPES.SICK]: { bg: "info", icon: "🤒" },
      [LEAVE_TYPES.PERSONAL]: { bg: "primary", icon: "👤" },
      [LEAVE_TYPES.VACATION]: { bg: "success", icon: "🏖️" },
      [LEAVE_TYPES.EMERGENCY]: { bg: "danger", icon: "🚨" },
      [LEAVE_TYPES.MATERNITY]: { bg: "pink", icon: "👶" },
      [LEAVE_TYPES.PATERNITY]: { bg: "blue", icon: "👨‍👶" },
      [LEAVE_TYPES.OTHER]: { bg: "secondary", icon: "📝" },
    };

    const config = typeConfig[type] || { bg: "secondary", icon: "📝" };
    return (
      <Badge bg={config.bg}>
        {config.icon} {type.charAt(0).toUpperCase() + type.slice(1)}
      </Badge>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Card className="shadow-sm">
        <Card.Body>
          {/* Filters */}
          <Row className="mb-3">
            <Col md={3}>
              <Form.Select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="">All Status</option>
                <option value={LEAVE_STATUS.PENDING}>Pending</option>
                <option value={LEAVE_STATUS.APPROVED}>Approved</option>
                <option value={LEAVE_STATUS.REJECTED}>Rejected</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.leaveType}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, leaveType: e.target.value }))
                }
              >
                <option value="">All Types</option>
                <option value={LEAVE_TYPES.SICK}>Sick Leave</option>
                <option value={LEAVE_TYPES.PERSONAL}>Personal</option>
                <option value={LEAVE_TYPES.VACATION}>Vacation</option>
                <option value={LEAVE_TYPES.EMERGENCY}>Emergency</option>
                <option value={LEAVE_TYPES.OTHER}>Other</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.dateRange}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, dateRange: e.target.value }))
                }
              >
                <option value="all">All Time</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 3 months</option>
              </Form.Select>
            </Col>
            <Col md={3} className="text-end">
              <Button variant="outline-primary" size="sm" onClick={fetchLeaves}>
                🔄 Refresh
              </Button>
            </Col>
          </Row>

          {/* Statistics */}
          <Row className="mb-3">
            <Col md={3}>
              <Alert variant="info" className="text-center mb-0">
                <strong>
                  {
                    leaves.filter((l) => l.status === LEAVE_STATUS.PENDING)
                      .length
                  }
                </strong>
                <br />
                <small>Pending</small>
              </Alert>
            </Col>
            <Col md={3}>
              <Alert variant="success" className="text-center mb-0">
                <strong>
                  {
                    leaves.filter((l) => l.status === LEAVE_STATUS.APPROVED)
                      .length
                  }
                </strong>
                <br />
                <small>Approved</small>
              </Alert>
            </Col>
            <Col md={3}>
              <Alert variant="danger" className="text-center mb-0">
                <strong>
                  {
                    leaves.filter((l) => l.status === LEAVE_STATUS.REJECTED)
                      .length
                  }
                </strong>
                <br />
                <small>Rejected</small>
              </Alert>
            </Col>
            <Col md={3}>
              <Alert variant="warning" className="text-center mb-0">
                <strong>{leaves.length}</strong>
                <br />
                <small>Total</small>
              </Alert>
            </Col>
          </Row>

          {/* Leaves Table */}
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Applied</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeaves.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center text-muted">
                      No leave applications found
                    </td>
                  </tr>
                ) : (
                  filteredLeaves.map((leave, index) => (
                    <tr key={leave.id}>
                      <td>{index + 1}</td>
                      <td>
                        <div>
                          <strong>{leave.userName}</strong>
                        </div>
                      </td>
                      <td>{getLeaveTypeBadge(leave.leaveType)}</td>
                      <td>{formatDate(leave.startDate)}</td>
                      <td>{formatDate(leave.endDate)}</td>
                      <td>
                        <Badge bg="light" text="dark">
                          {calculateLeaveDays(leave.startDate, leave.endDate)}{" "}
                          days
                        </Badge>
                      </td>
                      <td>{getStatusBadge(leave.status)}</td>
                      <td>{formatDate(leave.appliedAt)}</td>
                      <td>
                        {leave.status === LEAVE_STATUS.PENDING && (
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleAction(leave, "approve")}
                            >
                              <CheckIcon size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleAction(leave, "reject")}
                            >
                              <X size={16} />
                            </Button>
                          </div>
                        )}
                        {leave.status !== LEAVE_STATUS.PENDING && (
                          <Button
                            size="sm"
                            variant="outline-info"
                            onClick={() => {
                              setSelectedLeave(leave);
                              setActionType("view");
                              setShowModal(true);
                            }}
                          >
                            👁️
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Action Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === "approve" && "✅ Approve Leave"}
            {actionType === "reject" && "❌ Reject Leave"}
            {actionType === "view" && "👁️ Leave Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLeave && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Employee:</strong> {selectedLeave.userName}
                  <br />
                  <strong>Email:</strong> {selectedLeave.userEmail}
                </Col>
                <Col md={6}>
                  <strong>Type:</strong>{" "}
                  {getLeaveTypeBadge(selectedLeave.leaveType)}
                  <br />
                  <strong>Status:</strong>{" "}
                  {getStatusBadge(selectedLeave.status)}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <strong>Start Date:</strong>
                  <br />
                  {formatDate(selectedLeave.startDate)}
                </Col>
                <Col md={4}>
                  <strong>End Date:</strong>
                  <br />
                  {formatDate(selectedLeave.endDate)}
                </Col>
                <Col md={4}>
                  <strong>Total Days:</strong>
                  <br />
                  {calculateLeaveDays(
                    selectedLeave.startDate,
                    selectedLeave.endDate
                  )}{" "}
                  days
                </Col>
              </Row>

              <div className="mb-3">
                <strong>Reason:</strong>
                <p className="mt-2 p-2 bg-light rounded">
                  {selectedLeave.reason}
                </p>
              </div>

              {selectedLeave.emergencyContact && (
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Emergency Contact:</strong>
                    <br />
                    {selectedLeave.emergencyContact}
                  </Col>
                  <Col md={6}>
                    <strong>Emergency Phone:</strong>
                    <br />
                    {selectedLeave.emergencyPhone}
                  </Col>
                </Row>
              )}

              {selectedLeave.adminComments && (
                <div className="mb-3">
                  <strong>Admin Comments:</strong>
                  <p className="mt-2 p-2 bg-light rounded">
                    {selectedLeave.adminComments}
                  </p>
                </div>
              )}

              {(actionType === "approve" || actionType === "reject") && (
                <Form.Group>
                  <Form.Label>Comments (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Add any comments for the employee..."
                  />
                </Form.Group>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={actionLoading}
          >
            Close
          </Button>
          {(actionType === "approve" || actionType === "reject") && (
            <Button
              variant={actionType === "approve" ? "success" : "danger"}
              onClick={confirmAction}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {actionType === "approve" ? "Approving..." : "Rejecting..."}
                </>
              ) : (
                <>{actionType === "approve" ? "✅ Approve" : "❌ Reject"}</>
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
